import * as React from 'react';
import { cn } from '../lib/utils';

function Carousel({ className, children, ...props }: React.ComponentProps<'ul'>) {
	return (
		// `pan-y` is listed alongside `pan-x` so a vertical swipe that starts on a slide still scrolls
		// the page instead of being swallowed by the carousel (a plain `pan-x` traps the gesture: it
		// permits ONLY horizontal panning for touches beginning here, so the page would freeze under
		// the reader's finger).
		//
		// Permitting both axes is therefore right, and it is NOT what made a horizontal swipe drag the
		// page — that was the track claiming to scroll vertically as well. See `.carousel` in
		// globals.css: the axis discipline is the scroller's to keep, not this declaration's.
		//
		// It computes to `manipulation`, which is the same permission set; devtools showing that instead
		// is the browser canonicalising, not the rule failing to apply.
		<ul className={cn('carousel flex w-full gap-4 px-6 [touch-action:pan-x_pan-y_pinch-zoom]', className)} {...props}>
			{children}
		</ul>
	);
}

/**
 * The width of one slide, and so the distance between two snap points. Measured off the gap between the
 * first two children rather than assumed to be the track's width, so it holds both for a carousel whose
 * slide IS the frame (the annual reports) and for one showing several at once (the milestone timeline),
 * and it stays right when the gap changes at a breakpoint.
 */
function carouselStep(el: HTMLUListElement) {
	const [first, second] = [el.children[0], el.children[1]] as (HTMLElement | undefined)[];
	return first && second ? second.offsetLeft - first.offsetLeft : el.clientWidth;
}

function CarouselItem({ className, ...props }: React.ComponentProps<'li'>) {
	return <li className={cn('shrink-0', className)} {...props} />;
}

export { Carousel, CarouselItem, carouselStep };
